/**
 * Isochrone Cache Manager
 * 
 * Provides in-memory caching for isochrone data to reduce API calls and prevent
 * unnecessary usage counter increments when replaying animations.
 */

import type { Coordinates, TravelMode } from '../types';

/**
 * Cache manager interface for isochrone data
 */
export interface CacheManager {
  /**
   * Generates a cache key from coordinates, time, and travel mode
   * @param origin - Origin coordinates
   * @param time - Travel time in minutes
   * @param mode - Travel mode
   * @returns Cache key string
   */
  generateCacheKey(origin: Coordinates, time: number, mode: TravelMode): string;

  /**
   * Retrieves cached data by key
   * @param key - Cache key
   * @returns Cached polygon or undefined if not found
   */
  get(key: string): GeoJSON.Polygon | undefined;

  /**
   * Stores data in cache
   * @param key - Cache key
   * @param data - Polygon data to cache
   */
  set(key: string, data: GeoJSON.Polygon): void;

  /**
   * Checks if a key exists in cache
   * @param key - Cache key
   * @returns True if key exists, false otherwise
   */
  has(key: string): boolean;

  /**
   * Clears all cached data
   */
  clear(): void;

  /**
   * Returns the number of items in cache
   * @returns Cache size
   */
  size(): number;
}

/**
 * Isochrone Cache Manager Implementation
 * 
 * Uses in-memory Map for session-based caching. Coordinates are rounded to
 * 6 decimal places for consistent cache key generation.
 */
class IsochroneCacheManager implements CacheManager {
  private cache: Map<string, GeoJSON.Polygon> = new Map();

  /**
   * Generates a cache key from coordinates, time, and travel mode.
   * Coordinates are rounded to 6 decimal places for consistency.
   * 
   * @param origin - Origin coordinates
   * @param time - Travel time in minutes
   * @param mode - Travel mode
   * @returns Cache key in format: "lat,lng_time_mode"
   */
  generateCacheKey(origin: Coordinates, time: number, mode: TravelMode): string {
    // Round coordinates to 6 decimal places for cache key consistency
    const lat = origin.lat.toFixed(6);
    const lng = origin.lng.toFixed(6);
    return `${lat},${lng}_${time}_${mode}`;
  }

  /**
   * Retrieves cached data by key
   * @param key - Cache key
   * @returns Cached polygon or undefined if not found
   */
  get(key: string): GeoJSON.Polygon | undefined {
    return this.cache.get(key);
  }

  /**
   * Stores data in cache
   * @param key - Cache key
   * @param data - Polygon data to cache
   */
  set(key: string, data: GeoJSON.Polygon): void {
    this.cache.set(key, data);
  }

  /**
   * Checks if a key exists in cache
   * @param key - Cache key
   * @returns True if key exists, false otherwise
   */
  has(key: string): boolean {
    return this.cache.has(key);
  }

  /**
   * Clears all cached data
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Returns the number of items in cache
   * @returns Cache size
   */
  size(): number {
    return this.cache.size;
  }
}

/**
 * Singleton instance of the isochrone cache manager
 */
export const isochroneCache: CacheManager = new IsochroneCacheManager();


